import { BaseApi } from "../../utils/BaseApi";
import { ChatParams } from "../models/Chat/ChatParams";
import { MessageParams } from "../models/Chat/MessageParams";
import { HttpMethodEnum } from "../models/HttpMethodEnum";

export interface TestChatMetadataPayload {
  notes?: string;
  labels?: Array<{
    id: string;
    name: string;
    color: string;
  }>;
  evaluation?: {
    rating?: number;
    title?: string;
    description?: string;
    finalizedAt?: string | Date | null;
  };
}

export interface TestChatContextModel {
  companyId: string;
  wsSettings: {
    phoneNumberId?: string;
    whatsappId?: string;
    accessTokenMasked?: string;
  };
}

export interface TestChatReplayTurnModel {
  turnIndex: number;
  userMessage: string;
  messageType: string;
  baselineToolNames: string[];
  replayToolNames: string[];
  missingTools: string[];
  unexpectedTools: string[];
  exactToolMatch: boolean;
  baselineAssistantResponse: string;
  replayAssistantResponse: string;
}

export interface TestChatReplayStateModel {
  userRegistered: boolean;
  productsSelected: boolean;
  productsCount: number;
  deliveryType: string | null;
  addressRegistered: boolean;
  pickupStoreRegistered: boolean;
  paymentMethod: string | null;
  transferReceiptAttached: boolean;
  orderProgrammed: boolean;
  dateToProgram: number | null;
  orderId: string | null;
}

export interface TestChatReplayRunModel {
  id: string;
  sourceServiceCode: string;
  replayServiceCode?: string;
  replayPhoneNumber: string;
  createdAt: string | Date;
  completedAt: string | Date;
  status: "completed" | "failed";
  sourceUserTurns: number;
  replayUserTurns: number;
  exactToolMatch: boolean;
  exactTurnToolMatch: boolean;
  baselineToolSequence: string[];
  replayToolSequence: string[];
  missingTools: string[];
  unexpectedTools: string[];
  turns: TestChatReplayTurnModel[];
  sourceFinalState: TestChatReplayStateModel | null;
  replayFinalState: TestChatReplayStateModel | null;
  stateDifferences: string[];
  baselineLastAssistantResponse: string;
  replayLastAssistantResponse: string;
  error?: string;
}

export interface TestChatReplayBatchResult {
  sourceServiceCode: string;
  iterations: number;
  runs: TestChatReplayRunModel[];
}

export interface ChatSendResponse {
  sent: boolean;
  requiresTemplate?: boolean;
  errorMessage?: string;
  metaCode?: number | string;
  conversationWindowOpen?: boolean;
}

export interface ChatTypingResponse {
  sent: boolean;
}

export interface ChatAudioTranscriptionResponse {
  messageId: string;
  transcription: string;
}

export interface MaposOrderSummary {
  orderId: string;
  branchName: string;
  paymentStatus: string;
  paymentMethod: string;
  orderStatus: string;
  address: string;
  timestamp: string | Date;
  products: Array<{ name: string; subtotal: number; quantity: number }>;
}

export interface MaposOrdersResponse {
  orders: MaposOrderSummary[];
  count: number;
  limit: number;
  error?: string;
}

export type SupportTicketStatus = "OPEN" | "IN_PROGRESS" | "RESOLVED";

export interface SupportTicketSummary {
  id: string;
  code: string;
  phoneNumber: string;
  serviceCode?: string;
  description: string;
  imageUrl?: string;
  hasEvidenceImage?: boolean;
  orderId?: string;
  branchName?: string;
  status: SupportTicketStatus;
  createdAt: string | Date;
  updatedAt?: string | Date;
  resolvedAt?: string | Date | null;
}

export interface SupportTicketsResponse {
  tickets: SupportTicketSummary[];
  count: number;
  statusCounts?: Partial<Record<Extract<SupportTicketStatus, "OPEN" | "IN_PROGRESS">, number>>;
  nextCursor?: string;
  error?: string;
}

class ChatApi extends BaseApi {
  private static instance: ChatApi;

  public static get(): ChatApi {
    if (!this.instance) {
      this.instance = new ChatApi();
    }
    return this.instance;
  }

  public getAllChats = BaseApi.createEndpoint<any, null>(
    HttpMethodEnum.GET,
    "/chat/all"
  );

  public getChat = BaseApi.createEndpoint<any, ChatParams>(
    HttpMethodEnum.GET,
    "/chat"
  );

  public getMaposOrders = BaseApi.createEndpoint<
    MaposOrdersResponse,
    { phoneNumber: string; orderId?: string; limit?: number }
  >(HttpMethodEnum.GET, "/chat/orders");

  public resolveSupportTicket = BaseApi.createEndpoint<
    { resolved: boolean },
    { ticketCode: string }
  >(HttpMethodEnum.POST, "/chat/support/resolve");

  public getSupportTickets = BaseApi.createEndpoint<
    SupportTicketsResponse,
    { status?: SupportTicketStatus; phoneNumber?: string; ticketCode?: string; before?: string; limit?: number }
  >(HttpMethodEnum.GET, "/chat/support");

  public updateSupportTicketStatus = BaseApi.createEndpoint<
    { updated: boolean },
    { ticketCode: string; status: SupportTicketStatus }
  >(HttpMethodEnum.POST, "/chat/support/status");

  public updateChat = BaseApi.createEndpoint<any, ChatParams>(
    HttpMethodEnum.POST,
    "/chat"
  );

  public updateContact = BaseApi.createEndpoint<
    { updated: boolean },
    ChatParams & {
      customName?: string;
      profilePictureUrl?: string;
      notes?: string;
      tags?: string[];
    }
  >(HttpMethodEnum.POST, "/chat/contact");

  public sendMessage = BaseApi.createEndpoint<ChatSendResponse, MessageParams>(
    HttpMethodEnum.POST,
    "/chat/message"
  );

  public sendTyping = BaseApi.createEndpoint<
    ChatTypingResponse,
    { messageId: string }
  >(HttpMethodEnum.POST, "/chat/typing");

  public transcribeAudioMessage = BaseApi.createEndpoint<
    ChatAudioTranscriptionResponse,
    { phoneNumber: string; messageId: string }
  >(HttpMethodEnum.POST, "/chat/message/transcribe");

  public sendTestMessage = BaseApi.createEndpoint<
    any,
    Omit<MessageParams, "phoneNumber">
  >(HttpMethodEnum.POST, "/chat/test/message");

  public getTestChatContext = BaseApi.createEndpoint<TestChatContextModel, null>(
    HttpMethodEnum.GET,
    "/chat/test/context"
  );

  public resetTestChat = BaseApi.createEndpoint<any, { deleteHistory?: boolean }>(
    HttpMethodEnum.POST,
    "/chat/test/reset"
  );

  public updateTestSessionMetadata = BaseApi.createEndpoint<
    any,
    {
      serviceCode: string;
      metadata: TestChatMetadataPayload;
    }
  >(HttpMethodEnum.POST, "/chat/test/session/metadata");

  public replayTestSession = BaseApi.createEndpoint<
    TestChatReplayBatchResult,
    {
      serviceCode: string;
      iterations?: number;
    }
  >(HttpMethodEnum.POST, "/chat/test/session/replay");
}

export const chatApi = ChatApi.get();
